import { createTheme } from '@mui/material/styles'
import { breakpoints } from './Breakpoints'
import { tonalPalettes } from './Colors'
import { fonts, pixelToRemMap, typography as zTypography } from './Typography'
import { space } from './Core'
import { dFlexCenter } from './UtilityStyles'
import { createMuiButtonOverrides } from './overrides/MuiButtonOverrides'

type RGBAValue = `rgba(${number}, ${number}, ${number}, ${number})`

const generateColours = <TName extends string>(
  ...rgbValues: [TName, number, number, number][]
): {
  [key in TName]: (
    /** Decimal format (e.g. 20% is '0.2') */ transparency?: number
  ) => RGBAValue
} => {
  // @ts-expect-error "key in TName" complains because we are initialising as empty.
  const colourMap: { [key in TName]: (transparency?: number) => RGBAValue } = {}

  rgbValues.forEach(([name, red, green, blue]) => {
    colourMap[name] = (transparency = 1) =>
      `rgba(${red}, ${green}, ${blue}, ${transparency})`
  })

  return colourMap
}

export const muiColors = {
  primary: {
    white: '#fff',
    blueMain: 'rgb(31, 46, 209)',
    brightBlue: '#2E51D3',
    lightBlue: '#ECEFFB',
    blackAlt: '#333',
    blackAltOpacity80: 'rgba(51, 51, 51, 0.8)', // #333333CC
    blackAltOpacity60: 'rgba(51, 51, 51, 0.6)',
    blackAltOpacity23: 'rgba(51, 51, 51, 0.23)',
    errorRedMain: '#940000',
    lightGrey: 'rgba(209, 209, 209, 0.09)',
    lightestGrey: 'rgba(0, 0, 0, 0.04)',
    grey: '#6B6B6B',
    darkGrey: 'rgba(0, 0, 0, 0.09)',
    disabledGrey: 'rgba(0, 0, 0, 0.38)',
    green: 'rgb(88, 196, 198)',
    successGreen: 'rgba(101, 198, 89, 1)', // #65C659
    darkGreen: '#00B18C',
    yellow: 'rgba(255, 236, 0, 1)' // #FFEC00
  },
  secondary: {
    lightGrey: 'rgb(224, 224, 224)'
  },
  prominent: generateColours(
    ['blue', 31, 46, 209],
    ['darkBlue', 13, 20, 89],
    ['lightGrey', 224, 224, 224],
    ['darkGrey', 51, 51, 51]
  ),
  prominentColors: {
    lightBlue: '#EAECFC'
  }
}

export const muiTypography = {
  body: {
    label: {
      fontSize: '16px',
      fontWeight: 'normal',
      lineHeight: '24px',
      fontFamily: fonts.regular,
      letterSpacing: '0.4px',
      small: {
        fontSize: '12px',
        lineHeight: '14px',
        fontFamily: fonts.medium
      }
    },
    checkbox: {
      label: {
        normal: {
          fontSize: '16px',
          lineHeight: '24px',
          fontFamily: fonts.regular,
          letterSpacing: '0px'
        }
      }
    },
    text: {
      normal: {
        fontSize: '14px',
        lineHeight: '20px',
        fontFamily: fonts.regular
      },
      medium: {
        fontSize: '16px',
        lineHeight: '14px',
        fontFamily: fonts.medium
      },
      small: {
        fontSize: '9px',
        lineHeight: '14px',
        fontFamily: fonts.medium
      }
    }
  }
}

/* TODO: These should be in the theme only but short term solution until Npm package is working */
// Update MUI Theme breakpoints
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true
    sm: true
    md: true
    mdplus: true
    lg: true
    xsDesktop: true
    desktop: true
    wide: true
    xl: true
    ultraWide: true
  }
}

// Update the Typography's variant prop options in line with M3's design system
declare module '@mui/material/styles' {
  interface TypographyVariants {
    rowHeading: React.CSSProperties
    displayLarge: React.CSSProperties
    displayMedium: React.CSSProperties
    displaySmall: React.CSSProperties
    headlineLarge: React.CSSProperties
    headlineMedium: React.CSSProperties
    headlineSmall: React.CSSProperties
    headlineXS: React.CSSProperties
    titleLarge: React.CSSProperties
    titleMedium: React.CSSProperties
    titleSmall: React.CSSProperties
    labelXLarge: React.CSSProperties
    labelLarge: React.CSSProperties
    labelMedium: React.CSSProperties
    labelSmall: React.CSSProperties
    bodyLarge: React.CSSProperties
    bodyMedium: React.CSSProperties
    bodySmall: React.CSSProperties
  }
  interface TypographyVariantsOptions {
    rowHeading?: React.CSSProperties
    displayLarge: React.CSSProperties
    displayMedium: React.CSSProperties
    displaySmall: React.CSSProperties
    headlineLarge: React.CSSProperties
    headlineMedium: React.CSSProperties
    headlineSmall: React.CSSProperties
    headlineXS: React.CSSProperties
    titleLarge: React.CSSProperties
    titleMedium: React.CSSProperties
    titleSmall: React.CSSProperties
    labelXLarge: React.CSSProperties
    labelLarge: React.CSSProperties
    labelMedium: React.CSSProperties
    labelSmall: React.CSSProperties
    bodyLarge: React.CSSProperties
    bodyMedium: React.CSSProperties
    bodySmall: React.CSSProperties
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    rowHeading: true
    displayLarge: true
    displayMedium: true
    displaySmall: true
    headlineLarge: true
    headlineMedium: true
    headlineSmall: true
    headlineXS: true
    titleLarge: true
    titleMedium: true
    titleSmall: true
    labelXLarge: true
    labelLarge: true
    labelMedium: true
    labelSmall: true
    bodyLarge: true
    bodyMedium: true
    bodySmall: true
  }
}

// Hover styles are not applied to mobile/tablet
// Using the following breakpoints:
// becomes tablet at: '768px',
// becomes desktop at: '1025px'
const DefaultTheme = createTheme({
  ...breakpoints,
  spacing: [0, 4, 8, 10, 12, 16, 20, 24, 32, 40, 48, 56, 64, 96, 128],
  typography: {
    ...zTypography,
    h2: {
      fontSize: '34px',
      lineHeight: '42px',
      fontFamily: fonts.regular,
      color: muiColors.primary.blackAlt
    },
    h3: {
      fontSize: '18px',
      fontFamily: fonts.medium,
      color: muiColors.primary.blackAlt,
      '@media (min-width: 768px)': {
        fontSize: '20px'
      }
    },
    body1: {
      ...muiTypography.body.text.normal
    },
    body2: {
      fontSize: '14px',
      lineHeight: '20px',
      fontFamily: fonts.medium
    },
    rowHeading: {
      fontSize: '16px',
      lineHeight: '24px',
      fontFamily: fonts.medium
    }
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          // Map the new variant to render a <p> by default
          rowHeading: 'p'
        }
      }
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '&.disabled': {
            backgroundColor: 'unset',
            '.MuiInputBase-root::before, .MuiInputBase-root:hover::before': {
              borderBottom: `1px dashed ${muiColors.primary.disabledGrey}`
            },
            '&.MuiTextField-root': {
              '&:hover': {
                '.MuiFormLabel-root': {
                  color: muiColors.primary.disabledGrey
                }
              },
              '.MuiFormLabel-root': {
                color: muiColors.primary.disabledGrey
              }
            }
          },
          '&:not(.Mui-disabled):hover': {
            '.MuiFormLabel-root': {
              color: muiColors.primary.blackAlt,
              '&.Mui-error': {
                color: muiColors.primary.errorRedMain
              },
              '@media (min-width: 1025px)': {
                color: muiColors.primary.brightBlue
              }
            }
          },
          '& .MuiFormLabel-root': {
            ...muiTypography.body.label,
            pointerEvents: 'none',
            '&.MuiInputLabel-root.Mui-error': {
              color: muiColors.primary.errorRedMain
            },
            '&:not(.Mui-disabled)': {
              color: muiColors.primary.blackAlt,
              '&.MuiInputLabel-outlined': {
                color: muiColors.primary.blackAltOpacity80,
                '&.Mui-error': {
                  color: muiColors.primary.errorRedMain
                }
              },
              '&:not(.MuiInputLabel-shrink)': {
                color: muiColors.primary.blackAltOpacity60
              }
            },
            '&:not(.Mui-disabled).Mui-focused': {
              color: muiColors.primary.brightBlue,
              '&.MuiInputLabel-outlined': {
                color: muiColors.primary.blueMain
              }
            }
          },
          '&.outlined': {
            '& .MuiFormLabel-root': {
              '&:not(.Mui-disabled)': {
                '&.MuiInputLabel-outlined': {
                  '&.Mui-error': {
                    color: muiColors.primary.errorRedMain
                  }
                }
              }
            },
            '&.disabled': {
              '& .MuiFormLabel-root': {
                color: muiColors.primary.blackAltOpacity23
              },
              input: {
                color: muiColors.primary.blackAltOpacity23
              },
              '& fieldset': {
                border: `1px dotted ${muiColors.primary.blackAltOpacity23}`
              },
              '&:hover': {
                '& .MuiFormLabel-root': {
                  color: muiColors.primary.blackAltOpacity23
                },
                '& fieldset': {
                  borderColor: muiColors.primary.blackAltOpacity23
                }
              }
            },
            '&.MuiTextField-root': {
              margin: '0 0 10px 0',
              '.MuiFormHelperText-root': {
                fontSize: '12px',
                color: muiColors.primary.blackAltOpacity80,
                '&.Mui-error': {
                  color: muiColors.primary.errorRedMain
                }
              }
            },
            '& .MuiInputLabel-shrink': {
              paddingRight: '5px',
              backgroundColor: `${muiColors.primary.white}`
            }
          },
          '& .MuiInputBase-root': {
            fontFamily: muiTypography.body.label.fontFamily
          },
          '&.MuiTextField-root': {
            margin: '0 0 10px 0'
          },
          '&.MuiSelect-wrapper': {
            '& .MuiInputLabel-outlined': {
              background: `${muiColors.primary.white}`,
              '&.MuiInputLabel-shrink': {
                padding: '0 5px',
                marginLeft: '-3px'
              }
            }
          }
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          '& .MuiTypography-root': {
            ...muiTypography.body.checkbox.label.normal,
            color: muiColors.primary.grey
          },
          '& .MuiSvgIcon-root': {
            width: '18px',
            height: '18px'
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          ...muiTypography.body.label,
          marginLeft: '12px',
          '&.Mui-focused': {
            backgroundColor: 'unset'
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          // SHARED INPUT STYLES
          '&:not(.Mui-disabled) .MuiTypography-root': {
            color: muiColors.primary.blackAlt
          },
          'input[disabled]': {
            backgroundColor: muiColors.primary.white
          },
          input: {
            color: muiColors.primary.blackAlt,
            fontFamily: muiTypography.body.label.fontFamily
          },
          // OUTLINED INPUT SPECIFIC STYLES
          '&.MuiOutlinedInput-root': {
            borderRadius: '8px',
            '& fieldset': {
              borderColor: muiColors.primary.blackAlt
            },
            '&:hover': {
              '& fieldset': {
                borderColor: tonalPalettes.primary[60]
              }
            },
            '&.Mui-focused': {
              '& fieldset': {
                borderColor: muiColors.primary.blueMain
              },
              input: {
                color: muiColors.primary.blackAlt
              }
            },
            '&.Mui-error': {
              '& fieldset': {
                borderColor: muiColors.primary.errorRedMain
              }
            },
            input: {
              color: muiColors.primary.blackAlt,
              fontSize: '16px'
            },
            '& .MuiInputAdornment-root.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)':
              {
                '@media (min-width: 768px)': {
                  marginTop: '3px'
                }
              }
          },
          // FILLED INPUT SPECIFIC STYLES
          '&.MuiFilledInput-root': {
            backgroundColor: muiColors.primary.lightGrey,
            '&:after': {
              borderBottom: `2px solid ${muiColors.primary.brightBlue}`
            },
            '&.Mui-disabled': {
              backgroundColor: muiColors.primary.lightBlue
            },
            '&.Mui-focused:hover': {
              backgroundColor: muiColors.primary.lightBlue
            },
            '&:hover': {
              backgroundColor: muiColors.primary.lightGrey,
              '@media (min-width: 1025px)': {
                backgroundColor: muiColors.primary.lightBlue
              }
            },
            '& .MuiInputAdornment-root.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)':
              {
                marginTop: '15px',
                '@media (min-width: 768px)': {
                  marginTop: '19px'
                }
              }
          }
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            backgroundColor: muiColors.primary.lightBlue
          }
        },
        input: {
          '&:hover': {
            borderRadius: '4px 4px 0 0'
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          color: muiColors.primary.blackAlt,
          '&.MuiSelect-filled': {
            '&:focus': {
              backgroundColor: muiColors.primary.lightBlue
            }
          }
        }
      }
    },
    MuiList: {
      styleOverrides: {
        root: {
          '& .MuiButtonBase-root': {
            fontFamily: muiTypography.body.label.fontFamily
          }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          wordBreak: 'break-word',
          whiteSpace: 'break-spaces'
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          '& li': {
            ...muiTypography.body.checkbox.label.normal,
            '&.Mui-focused, &:hover': {
              backgroundColor: `${muiColors.primary.lightestGrey}`
            }
          }
        },
        option: {
          '&[aria-selected="true"]': {
            backgroundColor: `${muiColors.primary.lightestGrey}`
          }
        }
      }
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          ...muiTypography.body.text.normal,
          color: muiColors.primary.blackAlt,
          '@media (max-width: 359px)': {
            fontSize: '12px'
          }
        }
      }
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: muiColors.secondary.lightGrey
        }
      }
    },
    MuiButton: {
      styleOverrides: createMuiButtonOverrides(tonalPalettes)
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...dFlexCenter,
          flexDirection: 'column',
          borderRadius: '12px',
          border: `1px solid ${tonalPalettes.neutral[80]}`,
          boxShadow: 'none',
          padding: `${space.spacing[11]}px ${space.spacing[7]}px`,
          width: '100%',
          marginBottom: `${space.spacing[4]}px`,
          boxSizing: 'border-box',
          [theme.breakpoints.up('lg')]: {
            maxWidth: pixelToRemMap[360],
            marginBottom: `${space.spacing[10]}px`
          }
        })
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: () => ({
          marginBottom: '1px',
          '&.Mui-expanded': {
            marginBottom: `${space.spacing[6]}px`,
            borderBottom: `2px solid ${tonalPalettes.neutral[60]}`,
            '&:last-of-type': {
              marginBottom: `${space.spacing[6]}px`
            }
          }
        })
      }
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: () => ({
          margin: 0,
          padding: `${space.spacing[6]}px`
        }),
        content: () => ({
          margin: 0,
          zIndex: '-1',
          '&.Mui-expanded': {
            margin: 0
          }
        })
      }
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: () => ({
          ...zTypography.bodyMedium,
          padding: `0 ${space.spacing[6]}px ${space.spacing[6]}px`,
          p: {
            marginTop: 0,
            ...zTypography.bodyMedium
          }
        })
      }
    }
  },
  customPalette: tonalPalettes
})

export default DefaultTheme
